import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { ECoachChatSenderType } from '@gray/domain/models/coach/chat-message.model';
import { CoachChatService } from './coach-chat.service';
import { CoachChatSocketService, ICoachChatEvent } from './coach-chat-socket.service';

/**
 * The coach's unread student messages across every conversation, for the messages menu badge.
 *
 * Owns the chat socket while the panel layout is up. The gateway puts the connection in the
 * coach's own room, so messages from every student arrive here without joining their
 * conversations; the inbox and the messages tab listen on the same connection.
 */
@Injectable({providedIn: 'root'})
export class CoachChatUnreadService {
  private readonly total$ = new BehaviorSubject<number>(0);
  readonly unreadTotal$: Observable<number> = this.total$.asObservable();

  private readonly stopped$ = new Subject<void>();
  private running = false;
  /** The student whose conversation is on screen; their messages are read as they arrive. */
  private activeStudentId: number | null = null;

  constructor(
    private chatService: CoachChatService,
    private chatSocket: CoachChatSocketService,
  ) {
  }

  /** Starts counting and keeps the socket open; calling it again while running does nothing. */
  start(): void {
    if (this.running) {
      return;
    }
    this.running = true;

    // `ready` arrives on every (re)connect: recount whatever was missed while it was down.
    this.chatSocket.onReady()
      .pipe(takeUntil(this.stopped$))
      .subscribe(() => this.refresh());

    this.chatSocket.onEvent()
      .pipe(takeUntil(this.stopped$))
      .subscribe(event => this.onChatEvent(event));

    this.chatSocket.connectWithCurrentToken();
    this.refresh();
  }

  stop(): void {
    if (!this.running) {
      return;
    }

    this.running = false;
    this.stopped$.next();
    this.chatSocket.disconnect();
    this.total$.next(0);
  }

  setActiveStudent(studentId: number | null): void {
    this.activeStudentId = studentId;
  }

  /** Clears the active student only if it is still the given one (another view may have taken over). */
  clearActiveStudent(studentId: number): void {
    if (this.activeStudentId === studentId) {
      this.activeStudentId = null;
    }
  }

  refresh(): void {
    this.chatService.getUnreadCount()
      .pipe(takeUntil(this.stopped$))
      .subscribe({
        next: r => this.total$.next(r?.data?.total ?? 0),
        error: () => this.total$.next(0),
      });
  }

  private onChatEvent(event: ICoachChatEvent): void {
    const data = event?.data;

    if (event?.event === 'message' && data?.senderType === ECoachChatSenderType.Student
      && data.studentUserId !== this.activeStudentId) {
      this.total$.next(this.total$.value + 1);
    }

    // How much a read clears is only known per conversation, so ask the API for the new total.
    if (event?.event === 'read' && data?.side === ECoachChatSenderType.Coach) {
      this.refresh();
    }
  }
}
